































































































































import Vue from 'vue';
import { namespace } from 'vuex-class';
import { Component, Prop, Watch } from 'vue-property-decorator';
import { ValidationObserver, ValidationProvider } from 'vee-validate';

import Input from '@/shared/components/Inputs/Input.vue';
import Field from '@/shared/components/Field/Field.vue';
import Select from '@/shared/components/Inputs/Select.vue';
import Button from '@/shared/components/Button/Button.vue';
import ButtonBack from '@/shared/components/Button/ButtonBack.vue';
import ScreenSizeVisibility from '@/shared/components/ScreenSizeVisibility/ScreenSizeVisibility.vue';
import ReCAPTCHABranding from '@/shared/components/ReCAPTCHABranding/ReCAPTCHABranding.vue';

import { ECardStep, EPaymentMethod, EPaymentMethodTypes } from '@/helpers/enums';

import { ICreditCard } from '@/interfaces/CreditCard';
import { IPayment } from '@/interfaces/Payment';

import { number as creditCardNumber } from 'card-validator';

import { cardBrandByBrandName, cardNumberMaskByBrand, cardCVVMaskByBrand } from '@/helpers/card';

import { Payment } from '@/classes/Checkout/Payment';

import { Invoice } from '@/classes/Checkout/Invoice';
import { ICustomer } from '@/interfaces/Customer';

const AppModule = namespace('App');
const CheckoutModule = namespace('App/Checkout');
const BehaviorModule = namespace('App/Behavior');
const CustomerModule = namespace('App/Customer');

@Component({
  components: {
    Input,
    Select,
    Field,
    Button,
    ButtonBack,
    ScreenSizeVisibility,
    ReCAPTCHABranding,
    ValidationObserver,
    ValidationProvider
  }
})
export default class NewCreditCardForm extends Vue {
  public $refs!: {
    observer: any,
  };

  @Prop()
  private value: any;

  @Prop()
  private readonly hasCreditCards: boolean;

  @Prop()
  private readonly creditCardInstallments: [];

  @Prop()
  private readonly creditCardSelectedInstallment: any;

  @Prop()
  private readonly isRenewal?: boolean;

  @Prop()
  private readonly isSecondForm?: boolean;

  @AppModule.Mutation
  private setCardFirstDigits: Function;

  @AppModule.Mutation
  private setPaymentMethodSelected: Function;

  @CheckoutModule.Getter('selectedOfferPayment')
  private payment: Payment;

  @CheckoutModule.Getter('selectedOfferDiscountByPaymentMethod')
  private discountByPaymentMethod: any;

  @AppModule.Mutation
  private setStepFormSelected: Function;

  @BehaviorModule.Getter('isCaptchaValid')
  private isCaptchaValid: Function;

  @BehaviorModule.State
  private canPay: boolean;

  @CheckoutModule.Getter('selectedOfferInvoice')
  private invoice!: Invoice;

  @CustomerModule.State
  private customer: ICustomer;

  private card: ICreditCard = {
    card_flag: null,
    card_number: '',
    owner_name: '',
    cvv: '',
    expiration_date: '',
    installment: 1,
    token: null,
  };

  private errorForm: boolean = false;

  private get isMobile () {
    return this.$vuetify.breakpoint.smAndDown;
  }

  private get cardCVVMask() {
    return cardCVVMaskByBrand(this.card.card_flag);
  }

  private get cardNumberMask() {
    return cardNumberMaskByBrand(this.card.card_flag);
  }

  private hasAnyError (errors: any) {
    return Object.entries(errors).some((e: any) => e[1].length);
  }

  private get buttonLabel() {
    return this.isRenewal
      ? 'App.Checkout.Payment.CreditCard.finishRenew'
      : 'App.Checkout.Payment.confirmPayment';
  }

  private get labelDiscount() {
    return this.$t(`App.Checkout.Payment.discount.paymentsMethods.${EPaymentMethod.creditCard}`);
  }

  private get showLabelDiscount() {
    return this.discountByPaymentMethod(EPaymentMethod.creditCard);
  }

  private selectCard() {
    this.$emit('setCardStep', ECardStep.select);
  }

  private selectInstallment(installmentNumber: number) {
    this.$emit('setCardInstallment', installmentNumber);
  }

  private parseBrandName(brandName: string): string {
    switch (brandName) {
      case 'american-express':
        return 'amex';
      default:
        return brandName;
    }
  }

  private evaluateBrand(cardNumber?: string|number) {
    const number = creditCardNumber(String(cardNumber));

    if (number.card && number.card.type) {
      const brand = this.parseBrandName(number.card.type);

      this.card.card_flag = brand;

      if (this.isSecondForm) this.payment.setSecondCard(this.card);
      else this.payment.setCard(this.card);
    } else {
      if (this.isSecondForm) this.payment.setSecondCard({ ...this.card, card_flag: null });
      else this.payment.setCard({ ...this.card, card_flag: null });
    }
  }

  public async validateForm() {
    const valid: boolean = await this.$refs.observer.validate();

    valid && await this.generateTokenMercadoPago();

    return valid;
  }

  public async generateTokenMercadoPago() {
    try {
      const expirationDateSplit = String(this.card.expiration_date).split('/');

      const { id }=  await window.mp.createCardToken({
        cardNumber: String(this.card.card_number).replace(/\s/g,''),
        cardholderName: this.card.owner_name,
        cardExpirationMonth: expirationDateSplit[0],
        cardExpirationYear: expirationDateSplit[1],
        securityCode: String(this.card.cvv),
        identificationType: this.customer && this.$t(`App.Checkout.Payment.customerKind.${this.customer.kind}`),
        identificationNumber: this.customer && this.customer.document,
      });

      this.card.token = id;
    } catch(e) {
      console.error(e);
    }
  }

  @Watch('card', { deep: true })
  onCardChange (card: ICreditCard) {
    const creditCard = creditCardNumber(String(card.card_number));

    if (this.cardCVVMask.length !== String(card.cvv).length) {
      const correctCVV = String(card.cvv).substr(0, this.cardCVVMask.length);

      this.card.cvv = correctCVV;
    }

    if (!creditCard.isValid && String(this.card.card_number).replace(/\s/g,'').length !== this.cardNumberMask.replace(/\s/g,'').length
      && card.token !== '' &&  card.token !== null) {
      card.token = null;
    }

    this.$emit('input', this.card);

    this.evaluateBrand(card.card_number);
  }

  @Watch('card.card_number',{ immediate: true, deep:true })
  onNewCard (value: any){
    const creditCard = creditCardNumber(String(value));

    if (creditCard.isValid && !!creditCard.card && String(this.card.card_number).replace(/\s/g,'').length === this.cardNumberMask.replace(/\s/g,'').length) {
      this.setStepFormSelected(ECardStep.new);
      const cardFirstDigits = String(this.card.card_number).replace(/\s/g,'').substr(0, 8);
      this.setCardFirstDigits(cardFirstDigits);
      this.$emit('sendCardDigits');
    }
  }

  mounted() {
    this.setPaymentMethodSelected(EPaymentMethodTypes.creditCard);
  }
}
